import type { Lang } from '../i18n/types';

export type SeoMeta = {
  title: string;
  description: string;
  keywords: string;
  og_image?: string | null;
};

const SEO_DESCRIPTION_MAX = 10_000;

const BRAND_KEYWORDS: Record<Lang, string[]> = {
  zh: ['UniteStream', 'UniteBase', 'UniteGo', '具身智能'],
  en: ['UniteStream', 'UniteBase', 'UniteGo', 'embodied AI'],
};

const BLOG_LIST_KEYWORDS: Record<Lang, string[]> = {
  zh: ['博客', '机器人数据', '具身智能'],
  en: ['blog', 'robot data', 'embodied AI'],
};

const BLOG_POST_BASE_KEYWORDS: Record<Lang, string[]> = {
  zh: ['博客'],
  en: ['blog'],
};

function dedupeKeywords(parts: string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of parts) {
    for (const token of raw.split(/[,，、\s]+/)) {
      const k = token.trim();
      if (!k) continue;
      const key = k.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(k);
    }
  }
  return out.join(',');
}

export function buildKeywords(lang: Lang, custom: string | null | undefined, ...extra: string[]): string {
  return dedupeKeywords([custom ?? '', ...extra, ...BRAND_KEYWORDS[lang]]);
}

export function blogListSeoMeta(lang: Lang): SeoMeta {
  const isZh = lang === 'zh';
  return {
    title: isZh ? '博客 · UniteStream' : 'Blog · UniteStream',
    description: isZh
      ? 'UniteStream 官方博客：UniteBase、UniteGo 与具身智能实践分享。'
      : 'UniteStream blog: UniteBase, UniteGo, and embodied AI insights.',
    keywords: dedupeKeywords([...BLOG_LIST_KEYWORDS[lang], ...BRAND_KEYWORDS[lang]]),
  };
}

export type BlogPostSeoInput = {
  title: string;
  excerpt?: string | null;
  meta_description?: string | null;
  keywords?: string | null;
};

export function blogPostSeoMeta(lang: Lang, post: BlogPostSeoInput): SeoMeta {
  const isZh = lang === 'zh';
  const description =
    post.meta_description?.trim() ||
    post.excerpt?.trim() ||
    (isZh
      ? `${post.title} — UniteStream 官方博客。`
      : `${post.title} — UniteStream official blog.`);

  const titleTokens = post.title
    .split(/[\s\-—·|]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 1);

  const keywords = buildKeywords(
    lang,
    post.keywords,
    ...BLOG_POST_BASE_KEYWORDS[lang],
    ...titleTokens.slice(0, 4),
  );

  return {
    title: `${post.title} · UniteStream`,
    description: description.slice(0, SEO_DESCRIPTION_MAX),
    keywords,
  };
}
